import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { timezoneOptionsWithOffsets } from './timezoneCountries';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
const ISO_WITH_OFFSET_REGEX = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?[+-]\d{2}:\d{2}$/;
const TIMEZONE_OFFSET_REGEX = /[+-]\d{2}:\d{2}$/;
// RFC-like formats e.g., "TUE, 19 AUG 2025 05:14:52 AM UTC" or "Tue, 19 Aug 2025 05:14:52 UTC"
const RFC_WITH_AMPM_REGEX =
  /^[A-Za-z]{3},\s(\d{2})\s([A-Za-z]{3})\s(\d{4})\s(\d{2}):(\d{2}):(\d{2})\s(AM|PM)\s(UTC|GMT)$/i;
const RFC_24H_REGEX =
  /^[A-Za-z]{3},\s(\d{2})\s([A-Za-z]{3})\s(\d{4})\s(\d{2}):(\d{2}):(\d{2})\s(UTC|GMT)$/i;

const MONTHS_MAP: Record<string, string> = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
};

const timezoneByLabel = new Map<string, string>();
const timezoneByAbbreviation = new Map<string, string>();
const validTimezoneCache = new Map<string, boolean>();

timezoneOptionsWithOffsets.forEach((option) => {
  // Store both original and lowercase versions for case-insensitive lookup
  timezoneByLabel.set(option.label, option.value);
  timezoneByLabel.set(option.label.toLowerCase(), option.value);
  timezoneByAbbreviation.set(option.abbreviation, option.value);
  timezoneByAbbreviation.set(option.abbreviation.toLowerCase(), option.value);
});

/**
 * Timezone converter utility using Day.js that converts UTC date to user timezone
 * @param {string} utcDate - Date string in UTC format
 * @param {string} userTimezone - User's timezone (e.g., 'IST', 'PST', 'EST', etc.)
 * @param {boolean} [includeTime] - Optional boolean to include time in output (default: true)
 * @return {string | null} Formatted date string or null/'N/A' for invalid input
 */
export function timezoneConverter(
  utcDate: string | null,
  userTimezone: string,
  includeTime?: boolean,
  timeFormat?: boolean
): string | null {
  // Default includeTime to false if not provided (based on original implementation)
  const shouldIncludeTime = includeTime === undefined ? false : includeTime;
  if (!userTimezone) {
    userTimezone = 'Africa/Abidjan';
  }

  if (!utcDate || typeof utcDate !== 'string') {
    return 'N/A';
  }

  let parsedDate: dayjs.Dayjs;
  try {
    // Handle various UTC date formats
    const dateStr = utcDate.trim();
    // Normalize T and Z to uppercase for consistent handling (case-insensitive)
    // Replace 't' with 'T' and 'z' at the end with 'Z'
    const normalizedDateStr = dateStr.replace(/t/gi, 'T').replace(/z$/i, 'Z');
    // 1) RFC-like strings: convert to ISO 8601 Z first
    if (RFC_WITH_AMPM_REGEX.test(dateStr) || RFC_24H_REGEX.test(dateStr)) {
      let isoDateStr: string | null = null;
      const ampmMatch = dateStr.match(RFC_WITH_AMPM_REGEX);
      if (ampmMatch) {
        const [, dd, monStr, yyyy, hhRaw, mm, ss, ampm] = ampmMatch;
        const month = MONTHS_MAP[monStr.toLowerCase()];
        const hhNum = parseInt(hhRaw, 10) % 12 + (ampm.toUpperCase() === 'PM' ? 12 : 0);
        const hh = String(hhNum).padStart(2, '0');
        isoDateStr = `${yyyy}-${month}-${dd}T${hh}:${mm}:${ss}Z`;
      } else {
        const match24 = dateStr.match(RFC_24H_REGEX);
        if (match24) {
          const [, dd, monStr, yyyy, hh, mm, ss] = match24;
          const month = MONTHS_MAP[monStr.toLowerCase()];
          isoDateStr = `${yyyy}-${month}-${dd}T${hh}:${mm}:${ss}Z`;
        }
      }
      if (!isoDateStr) {
        return 'N/A';
      }
      parsedDate = dayjs.utc(isoDateStr);
    } else if (ISO_WITH_OFFSET_REGEX.test(normalizedDateStr)) {
      // 2) Check if it's already in ISO format with timezone offset (e.g., 2025-02-01T00:00:00.000+00:00)
      // Parse ISO format with timezone offset directly
      parsedDate = dayjs(normalizedDateStr).utc();
    } else if (normalizedDateStr.includes('T')) {
      // Handle ISO format without timezone (add Z if not present)
      const finalDateStr =
        normalizedDateStr + (normalizedDateStr.includes('Z') || TIMEZONE_OFFSET_REGEX.test(normalizedDateStr) ? '' : 'Z');
      parsedDate = dayjs.utc(finalDateStr);
    } else if (/\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}/.test(dateStr)) {
      // Handle DD-MM-YYYY HH:mm:ss format (e.g., '12-08-2025 10:39:35')
      const [datePart, timePart] = dateStr.split(' ');
      const [day, month, year] = datePart.split('-');
      const isoDateStr = `${year}-${month}-${day}T${timePart}Z`;
      parsedDate = dayjs.utc(isoDateStr);
    } else {
      // Handle other space-separated formats
      const isoDateStr = dateStr.replace(' ', 'T') + 'Z';
      parsedDate = dayjs.utc(isoDateStr);
    }
    if (!parsedDate.isValid()) {
      return 'N/A';
    }
  } catch (error) {
    return 'N/A';
  }

  if (!userTimezone || typeof userTimezone !== 'string' || userTimezone.trim() === '') {
    // Use original UTC date without any timezone conversion - just format
    const formattedDate = shouldIncludeTime
      ? timeFormat ? parsedDate.utc().format('DD MMM YYYY, hh:mm A') : parsedDate.utc().format('DD MMM YYYY, HH:mm')
      : parsedDate.utc().format('DD MMM, YYYY');

    return formattedDate;
  } else {
    const isValidUserTimezone = isValidTimezoneDayjs(userTimezone);

    let targetDate: dayjs.Dayjs;
    if (isValidUserTimezone) {
      // Convert to target timezone
      targetDate = parsedDate.tz(userTimezone);
    } else {
      // Use original UTC date without timezone conversion
      targetDate = parsedDate.utc();
    }

    // Format the date
    const formattedDate = shouldIncludeTime
      ? timeFormat ? targetDate.format('DD MMM, YYYY hh:mm A') : targetDate.format('DD MMM YYYY, HH:mm')
      : targetDate.format('DD MMM, YYYY');

    return formattedDate;
  }
}

/**
 * Helper function to validate if a timezone string is valid using Day.js with caching
 * @param {string} timezone - Timezone string to validate
 * @return {boolean} boolean indicating if timezone is valid
 */
export function isValidTimezoneDayjs(timezone: string): boolean {
  // Check cache first to avoid repeated validation
  if (validTimezoneCache.has(timezone)) {
    const cachedResult = validTimezoneCache.get(timezone);
    return cachedResult !== undefined ? cachedResult : false;
  }

  let isValid = false;
  try {
    // Try to create a date in the specified timezone
    const testDate = dayjs().tz(timezone);
    isValid = testDate.isValid();
  } catch (error) {
    isValid = false;
  }

  // Cache the result for future calls
  validTimezoneCache.set(timezone, isValid);
  return isValid;
}
